<template>
  <div :class="{ 'is-mobile': isMobile }">
    <v-app>
      <template v-if="!isLoggedIn || isFetchingData">
        <login-component></login-component>
      </template>
      <template v-else>
        <app-navigation></app-navigation>
        <speed-dial></speed-dial>
        <router-view v-if="!isCurrentRouteRestricted" />
        <restricted-page v-else></restricted-page>
        <connection-status></connection-status>
        <common-confirmation-dialog></common-confirmation-dialog>
        <generic-input-dialog></generic-input-dialog>
        <customer-dialog></customer-dialog>
        <booking-dialog></booking-dialog>
        <booking-group-dialog></booking-group-dialog>
        <booking-cancellation-dialog></booking-cancellation-dialog>
        <global-reservations-dialogs></global-reservations-dialogs>
        <checkin-dialog></checkin-dialog>
        <late-checkout-dialog></late-checkout-dialog>
        <late-checkin-dialog></late-checkin-dialog>
        <invoice-dialog></invoice-dialog>
        <receipt-dialog></receipt-dialog>
        <product-form-dialog></product-form-dialog>
        <product-group-form-dialog></product-group-form-dialog>
        <product-dialog></product-dialog>
        <product-group-dialog></product-group-dialog>
        <position-block-dialog></position-block-dialog>
        <position-block-form-dialog></position-block-form-dialog>
        <print-preview-dialog></print-preview-dialog>
        <terminal-status-dialog></terminal-status-dialog>
        <receipt-delivery-dialog></receipt-delivery-dialog>
        <booking-form-dialog></booking-form-dialog>
        <email-preview-dialog></email-preview-dialog>
      </template>

      <snackbar-collection></snackbar-collection>
      <page-loader></page-loader>
      <update-dialog></update-dialog>
      <pwa-prompt></pwa-prompt>
      <escape-hold-indicator></escape-hold-indicator>
    </v-app>
  </div>
</template>

<style lang="scss">
@import './sass/app.scss';
</style>

<script>
import { mapState, mapGetters } from 'vuex'

import PageLoader from '@/components/layout/PageLoader.vue'
import LoginComponent from './views/Login.vue'
import AppNavigation from '@/components/layout/AppNavigation.vue'
import SnackbarCollection from '@/components/common/SnackbarCollection.vue'
import ConnectionStatus from '@/components/common/ConnectionStatus.vue'
import UpdateDialog from '@/components/common/UpdateDialog.vue'
import PwaPrompt from '@/components/common/PwaPrompt.vue'
import CommonConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import GenericInputDialog from '@/components/common/GenericInputDialog.vue'
import RestrictedPage from '@/components/common/RestrictedPage.vue'
import SpeedDial from '@/components/layout/SpeedDial.vue'
import EscapeHoldIndicator from '@/components/layout/EscapeHoldIndicator.vue'
import GlobalReservationsDialogs from '@/components/reservations/GlobalReservationsDialogs.vue'

// Dialoger som behövs på flera sidor
import CustomerDialog from '@/components/customer/Dialog.vue'
import BookingDialog from '@/components/booking/Dialog.vue'
import BookingGroupDialog from '@/components/booking-group/Dialog.vue'
import BookingCancellationDialog from '@/components/cancellation-policy/BookingCancellationDialog.vue'
import InvoiceDialog from '@/components/invoice/Dialog.vue'
import ReceiptDialog from '@/components/receipt/Dialog.vue'
import CheckinDialog from '@/components/booking/CheckinDialog.vue'
import LateCheckoutDialog from '@/components/booking/LateCheckoutDialog.vue'
import LateCheckinDialog from '@/components/booking/LateCheckinDialog.vue'
import ProductFormDialog from '@/components/product/ProductFormDialog.vue'
import ProductGroupFormDialog from '@/components/product/ProductGroupFormDialog.vue'
import ProductDialog from '@/components/product/Dialog.vue'
import ProductGroupDialog from '@/components/product/ProductGroupDialog.vue'
import PositionBlockFormDialog from '@/components/position/PositionBlockFormDialog.vue'
import PositionBlockDialog from '@/components/position/PositionBlockDialog.vue'
import PrintPreviewDialog from '@/components/common/PrintPreviewDialog.vue'
import EmailPreviewDialog from '@/components/common/EmailPreviewDialog.vue'
import BookingFormDialog from '@/components/booking/Form.vue'
import TerminalStatusDialog from '@/components/terminal/StatusDialog.vue'
import ReceiptDeliveryDialog from '@/components/register/ReceiptDeliveryDialog.vue'

export default {
  components: {
    PageLoader,
    AppNavigation,
    LoginComponent,
    SnackbarCollection,
    UpdateDialog,
    ConnectionStatus,
    CommonConfirmationDialog,
    GenericInputDialog,
    RestrictedPage,
    SpeedDial,
    EscapeHoldIndicator,
    GlobalReservationsDialogs,
    CustomerDialog,
    BookingDialog,
    BookingGroupDialog,
    BookingCancellationDialog,
    InvoiceDialog,
    ReceiptDialog,
    CheckinDialog,
    LateCheckoutDialog,
    LateCheckinDialog,
    ProductFormDialog,
    ProductGroupFormDialog,
    ProductDialog,
    ProductGroupDialog,
    PositionBlockDialog,
    PositionBlockFormDialog,
    PrintPreviewDialog,
    EmailPreviewDialog,
    BookingFormDialog,
    TerminalStatusDialog,
    PwaPrompt,
    ReceiptDeliveryDialog
  },
  data: () => ({}),
  computed: {
    ...mapState(
      {
        isFetchingData: state => state.isFetchingData
      }
    ),
    ...mapGetters(
      {
        activeAuthSession: 'user/activeAuthSession',
        isAuthenticated: 'user/isAuthenticated',
        isMenuItemRestricted: 'user/isMenuItemRestricted'
      }
    ),
    currentRouteMenuKey: function () {
      const routeWithMenuKey = [...this.$route.matched]
        .reverse()
        .find(route => route.meta?.menuKey)

      return routeWithMenuKey?.meta.menuKey || null
    },
    isCurrentRouteRestricted: function () {
      return !!this.currentRouteMenuKey && this.isMenuItemRestricted(this.currentRouteMenuKey)
    },
    user: function () {
      return this.activeAuthSession?.user
    },
    isLoggedIn: function () {
      return this.isAuthenticated
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    }
  },
  created () {
    /*
    * Koden nedan är till för att se till att höjden (100vh) på sidan blir rätt i mobil browsers som inkluderar höjden på browserns egna fält ovanför sidan i beräkningen, så att 100vh blir typ 50px högre än sidans faktiska höjd. T.ex. android chrome måste man scrolla ner för att se logga ut knappen som är längst ner i menyn
    * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    **/
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    // We listen to the resize event
    window.addEventListener('resize', () => {
      // We execute the same script as before
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    })
  }
}
</script>
